import { Core } from "@faire/design-tokens";
import { ALREADY_OPEN_VALUE } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts";
import {
  OPEN_WITH_FAIRE_APPLICATION_DATA_KEY,
  OwFFormErrors,
  OwFFormFieldKey,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/index";
import {
  attachDocumentTokens,
  formDocumentToken,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/helpers";
import { OwFFormDataStorage } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormDataStorage";
import { OwFFormFieldValueType } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import { useAdditionalInfo } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useAdditionalInfo";
import { useBasicInfo } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useBasicInfo";
import { useFinancialInfo } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useFinancialInfo";
import { useGetOwFApplicationStoreType } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useGetOwFApplicationStoreType";
import { useIsNMIApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useIsSoftCreditCheckApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsSoftCreditCheckApplication";
import {
  OwFTier,
  useOwFIDVTierLogic,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFIDVTierLogic";
import { useStoreInfo } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useStoreInfo";
import {
  isNotPseudoKey,
  trackOwFApplicationFormFieldValueChange,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/utils";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { getTwitterClickId } from "@faire/retailer-visitor-shared/initializers/facebook";
import { useOpenWithFaireApplicationStatus } from "@faire/retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireApplicationStatus";
import {
  ErrorMessages,
  getErrorMessage,
  getWebApiErrorMessage,
} from "@faire/web/common/getErrorMessage";
import { throttleIfPossible } from "@faire/web/common/globals/throttleIfPossible";
import { useStrictLocalization } from "@faire/web/common/localization";
import { logError } from "@faire/web/common/logging";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import { useState } from "@faire/web/ui/hooks/useState";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import submitOpenWithFaireSoftCreditCheckApplication from "@faire/web-api--source/endpoints/www/api/launch-with-faire/soft-credit-check/submit-application/post";
import startOpenWithFaireApplication from "@faire/web-api--source/endpoints/www/api/launch-with-faire/start-launch-with-faire-application/post";
import submitOpenWithFaireAdditionalInfoApplication from "@faire/web-api--source/endpoints/www/api/launch-with-faire/submit-additional-info/post";
import submitOpenWithFaireApplication from "@faire/web-api--source/endpoints/www/api/launch-with-faire/submit-launch-with-faire-application/post";
import { trackOpenWithFaireOwfApplicationFieldValueChange } from "@faire/web-api--source/events/openWithFaire/actionUnknown/owfApplicationFieldValueChange";
import { trackOpenWithFaireSubmitApplicationTierClick } from "@faire/web-api--source/events/openWithFaire/click/submitApplicationTier";
import { trackOpenWithFaireOpenWithFaireApplicationSubmitSubmit } from "@faire/web-api--source/events/openWithFaire/submit/openWithFaireApplicationSubmit";
import { trackOpenWithFaireOwfApplicationContinueApplicationView } from "@faire/web-api--source/events/openWithFaire/view/owfApplicationContinueApplication";
import { trackOpenWithFaireOwfApplicationStartApplicationView } from "@faire/web-api--source/events/openWithFaire/view/owfApplicationStartApplication";
import { trackOpenWithFaireViewApplicationTierView } from "@faire/web-api--source/events/openWithFaire/view/viewApplicationTier";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import { IDisplayableMoneyRange } from "@faire/web-api--source/indigofair/data/IDisplayableMoneyRange";
import { IPixelCustomDataParameters } from "@faire/web-api--source/indigofair/data/IPixelCustomDataParameters";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import { IRetailerLaunchWithFaireApplicationRequest } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplicationRequest";
import { IRetailerLaunchWithFaireSoftCreditCheckApplicationRequest } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireSoftCreditCheckApplicationRequest";
import { QueryParameters as owfApplicationQueryParameters } from "@faire/web-api--source/routes/www/net-terms/owf-application";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import { format as formatDate } from "date-fns/format";
import isEmpty from "lodash/isEmpty";
import { useEffect, useCallback, useRef, useMemo } from "react";
import React from "react";

import { useOwFStore } from "./__internal__/OwFFormStore";

export enum FORM_VERSION {
  SOFT_CREDIT_CHECK = "SOFT_CREDIT_CHECK",
}

interface IOwFFormArgs {
  startingValues?: Partial<IRetailerLaunchWithFaireApplication>;
  isSignUp?: boolean;
  owfStoreType?: keyof typeof IRetailer.BusinessCategory;
}

export const useOwFForm = (args?: IOwFFormArgs) => {
  const { activeTier } = useOwFIDVTierLogic();
  const { strictLocalize } = useStrictLocalization();
  const { redirectedFrom } = useQueryParams(
    owfApplicationQueryParameters.parse
  );
  const { isEligible, canApply } = useOpenWithFaireApplicationStatus();
  const { isEuropeNonUk } = useNationality();
  const { retailer, retailerName, retailerToken, refetchRetailer } =
    useRetailer();
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  const { isNMIApplication } = useIsNMIApplication();
  const { owfStoreType } = useGetOwFApplicationStoreType();

  const formData = useOwFStore((state) => state.formData);
  const updateFormData = useOwFStore((state) => state.updateFormData);
  const updateFormField = useOwFStore((state) => state.updateFormField);
  const setLoading = useOwFStore((state) => state.setLoading);

  const isStoreAddressFormVisible = useOwFStore(
    (state) => state.isStoreAddressFormVisible
  );
  const setIsStoreAddressFormVisible = useOwFStore(
    (state) => state.setIsStoreAddressFormVisible
  );

  const storage = useRef(
    new OwFFormDataStorage(
      OPEN_WITH_FAIRE_APPLICATION_DATA_KEY,
      retailerToken!,
      formData
    )
  ).current;
  const [errors, setErrors] = useState<OwFFormErrors>({});
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(false);
  const [isResidentialAddressFormVisible, setIsResidentialAddressFormVisible] =
    useState<boolean>(false);

  // computed values
  const isRetailerOther =
    retailer?.business_category === IRetailer.BusinessCategory.BUSINESS_OTHER;
  const owfFormStoreType = isNMIApplication
    ? owfStoreType
    : formData.best_store_type;
  const retailerBusinessType = isRetailerOther
    ? (owfFormStoreType ?? IRetailer.BusinessCategory.ONLINE_ONLY)
    : retailer?.business_category;
  const isBrickAndMortar =
    retailerBusinessType === IRetailer.BusinessCategory.BRICK_AND_MORTAR;
  const isOnlineOnly =
    retailerBusinessType === IRetailer.BusinessCategory.ONLINE_ONLY;

  const eventPathBase = `/retailer/${retailerToken}/open-with-faire/application`;
  const formVersion = useMemo<
    keyof typeof FORM_VERSION | keyof typeof OwFTier
  >(() => {
    if (isSoftCreditCheckApplication) {
      return FORM_VERSION.SOFT_CREDIT_CHECK;
    }

    return activeTier;
  }, [activeTier, isSoftCreditCheckApplication]);

  // Effects
  const [hasRecordedEvent, setHasRecordedEvent] = useState<boolean>(false);
  const initialize = useCallback(async () => {
    if (hasRecordedEvent) {
      return;
    }

    if (isEligible) {
      try {
        await startOpenWithFaireApplication();
        await refetchRetailer();
        if (redirectedFrom) {
          trackOpenWithFaireOwfApplicationStartApplicationView(
            redirectedFrom,
            `${!!args?.isSignUp}`
          );
        } else {
          trackOpenWithFaireOwfApplicationStartApplicationView(
            "",
            `${!!args?.isSignUp}`
          );
        }
      } catch (error) {
        logError(error);
      }
    } else {
      if (redirectedFrom) {
        trackOpenWithFaireOwfApplicationContinueApplicationView(
          redirectedFrom,
          `${!!args?.isSignUp}`
        );
      } else {
        trackOpenWithFaireOwfApplicationContinueApplicationView(
          "",
          `${!!args?.isSignUp}`
        );
      }
    }
    setHasRecordedEvent(true);
    setLoading(false);
  }, [
    hasRecordedEvent,
    isEligible,
    refetchRetailer,
    redirectedFrom,
    args?.isSignUp,
    setLoading,
  ]);
  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (storage.formData) {
      updateFormData(storage.formData);
    }
    setIsResidentialAddressFormVisible(
      !isEmpty(storage?.formData?.residential_address) &&
        !storage?.formData?.residential_address_google_place_id
    );
    setIsStoreAddressFormVisible(
      !isEmpty(storage?.formData?.store_address) &&
        !storage?.formData?.store_address_google_place_id
    );
  }, [
    storage,
    setIsResidentialAddressFormVisible,
    setIsStoreAddressFormVisible,
    updateFormData,
  ]);

  const throttledStoreFormData = useRef(
    throttleIfPossible((newFormData: IRetailerLaunchWithFaireApplication) => {
      storage.setData(newFormData);
    }, 1000)
  );
  useEffect(() => {
    throttledStoreFormData.current(formData);
  }, [formData]);

  useEffect(() => {
    trackOpenWithFaireViewApplicationTierView(
      formVersion,
      `${!!args?.isSignUp}`
    );
  }, [args?.isSignUp, formVersion]);

  // functions
  const setValue = (
    fieldName: OwFFormFieldKey,
    newValue: OwFFormFieldValueType
  ): void => {
    // TODO @mleandres: temporary type casting to avoid type errors
    updateFormField(
      fieldName as keyof IRetailerLaunchWithFaireApplication,
      newValue as IRetailerLaunchWithFaireApplication[keyof IRetailerLaunchWithFaireApplication]
    );
    // todo: migrate OWF legacy events to PETE FD-194834
    trackEvent(`${eventPathBase}/value-change/::FIELD_NAME::${fieldName}`);
    trackOwFApplicationFormFieldValueChange(
      fieldName,
      formVersion,
      `${!!args?.isSignUp}`
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: undefined,
      message: undefined,
    }));
  };

  const setMoneyRangeValue = (
    fieldName: OwFFormFieldKey,
    moneyRangeOptions: IDisplayableMoneyRange[] | undefined,
    newValue: string
  ): void => {
    const index = parseInt(newValue, 10);
    if (isNaN(index)) {
      return;
    }
    const rangeField = moneyRangeOptions?.[index];
    if (!rangeField) {
      return;
    }

    const minFieldKey = `${fieldName}_min`;
    const maxFieldKey = `${fieldName}_max`;
    updateFormData({
      [minFieldKey]: rangeField.min_amount,
      [maxFieldKey]: rangeField.max_amount,
      [fieldName]: newValue,
    });
    // todo: migrate OWF legacy events to PETE FD-194834
    trackEvent(`${eventPathBase}/value-change/::FIELD_NAME::${fieldName}`);
    trackOpenWithFaireOwfApplicationFieldValueChange(
      fieldName,
      formVersion,
      `${!!args?.isSignUp}`
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: undefined,
      message: undefined,
    }));
  };

  const toggleResidentialAddressForm = () => {
    updateFormField("residential_address", undefined);
    setIsResidentialAddressFormVisible(!isResidentialAddressFormVisible);
    trackEvent(`${eventPathBase}/show-residential-address-form`);
  };

  const toggleStoreAddressForm = () => {
    updateFormField("store_address", undefined);
    setIsStoreAddressFormVisible(!isStoreAddressFormVisible);
  };

  // Field computed values
  const isSignUp = args?.isSignUp;
  const { visibleBasicInfoKeys, getBasicInfoErrors, basicInfoFields } =
    useBasicInfo({
      formData,
      setValue,
      isResidentialAddressFormVisible,
      toggleResidentialAddressForm,
      isSignUp: args?.isSignUp,
    });

  const { visibleStoreInfoKeys, getStoreInfoErrors, storeInfoFields } =
    useStoreInfo({
      formData,
      setValue,
      isStoreAddressFormVisible,
      toggleStoreAddressForm,
      isSignUp: args?.isSignUp,
    });
  const {
    visibleFinancialInfoKeys,
    getFinancialInfoErrors,
    financialInfoFields,
  } = useFinancialInfo({
    formData,
    setValue,
    isResidentialAddressFormVisible,
    toggleResidentialAddressForm,
    isSignUp: args?.isSignUp,
  });

  const { visibleAdditionalInfoKeys, additionalInfoFields } =
    useAdditionalInfo(formData);

  const firstFailureKey = [
    "full_name",
    "residential_address",
    "best_store_type",
    "store_name",
    "opening_range",
    "lease_signed",
    "online_has_store_website",
    "online_website_proof_documents",
    isOnlineOnly ? "website_url" : null,
    "customer_acquisition_plan",
    "marketing_amount",
    "store_hours_commitment",
    "popup_location_checkboxes",
    "popup_locations",
    "popup_lease",
    "lease_length",
    "monthly_rent",
    "popup_cost",
    "store_address",
    "popup_months_open",
    isEuropeNonUk ? "store_square_meterage" : "store_square_footage",
    "popup_days_per_week",
    "popup_hours_open",
    isBrickAndMortar ? "preferred_terms_amount" : null,
    "funding_amount",
    "funding_sources",
    "credit_card_limit",
    isBrickAndMortar ? null : "preferred_terms_amount",
    "credit_check_authorized",
  ]
    .filter(isNotNull)
    // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
    .find((key) => !!errors[key]);

  // Validate and submit
  const validate = (
    skippedSections: {
      skipBasicInfo?: boolean;
      skipStoreInfo?: boolean;
      skipFinancialInfo?: boolean;
    } = {}
  ): {
    isValid: boolean;
    isBasicInfoValid: boolean;
    isStoreInfoValid: boolean;
    isFinancialInfoValid: boolean;
  } => {
    setShouldAutoScroll(false);
    const basicInfoErrors = skippedSections.skipBasicInfo
      ? {}
      : getBasicInfoErrors();
    const hasBasicInfoError = Object.values(basicInfoErrors).length > 0;
    const storeInfoErrors = skippedSections.skipStoreInfo
      ? {}
      : getStoreInfoErrors();
    const hasStoreInfoError = Object.values(storeInfoErrors).length > 0;
    const financialInfoErrors = skippedSections.skipFinancialInfo
      ? {}
      : getFinancialInfoErrors();
    const hasFinancialInfoError = Object.values(financialInfoErrors).length > 0;
    const updatedErrors = {
      ...basicInfoErrors,
      ...storeInfoErrors,
      ...financialInfoErrors,
    };
    const hasError = Object.values(updatedErrors).length > 0;
    if (hasError) {
      setErrors({
        ...updatedErrors,
        message: !isSoftCreditCheckApplication
          ? strictLocalize({
              defaultMessage:
                "Looks like you missed some of the required fields.",
              description: {
                text: "Error message when the required field(s) of the form is not filled",
              },
            })
          : undefined,
      });
      setShouldAutoScroll(true);
    } else {
      setErrors(updatedErrors);
    }

    return {
      isValid: !hasError,
      isBasicInfoValid: !hasBasicInfoError,
      isStoreInfoValid: !hasStoreInfoError,
      isFinancialInfoValid: !hasFinancialInfoError,
    };
  };

  const mapFormDataToRequestData = (): IRetailerLaunchWithFaireApplication => {
    const {
      residential_address,
      residential_address_google_place_id,
      store_address,
      store_address_google_place_id,
      opening_range,
      full_name,
      store_name,
      date_of_birth,
      first_name,
      last_name,
    } = formData;

    const residentialAddress = convertAddressData(
      isResidentialAddressFormVisible,
      residential_address_google_place_id,
      residential_address
    );

    const storeAddress = convertAddressData(
      isStoreAddressFormVisible,
      store_address_google_place_id,
      store_address
    );

    // Do a deep clone before deleting form data that we are not sending to BE
    const allVisibleFields: string[] = [
      ...visibleBasicInfoKeys,
      ...visibleStoreInfoKeys,
      ...visibleFinancialInfoKeys,
      ...visibleAdditionalInfoKeys,
    ];

    const visibleData = (Object.keys(formData) as OwFFormFieldKey[])
      .filter((key) => allVisibleFields.includes(key))
      .reduce((visibleDataAcc, key) => {
        if (isNotPseudoKey(key)) {
          // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
          visibleDataAcc[key] = formData[key];
        }
        return visibleDataAcc;
      }, {});

    const visibleFormData = IRetailerLaunchWithFaireApplication.build({
      ...visibleData,
      popup_location_events: formData.popup_location_events,
      popup_location_brick_and_mortar: formData.popup_location_brick_and_mortar,
    });

    const formDataClone = { ...visibleFormData };

    const { store_square_footage, store_square_meterage, popup_days_per_week } =
      formDataClone;

    return {
      ...formDataClone,
      full_name: full_name,
      first_name: first_name,
      last_name: last_name,
      store_name: store_name ?? retailerName,
      documents: [],
      online_website_proof_documents: [],
      residential_address: residentialAddress,
      residential_address_google_place_id: !residentialAddress
        ? formData.residential_address_google_place_id
        : undefined,
      store_address: storeAddress,
      store_address_google_place_id: !storeAddress
        ? formData.store_address_google_place_id
        : undefined,
      opening_range: parseOpeningRange(opening_range),
      funding_amount: undefined,
      funding_amount_min: formData.funding_amount_min,
      funding_amount_max: formData.funding_amount_max,
      credit_card_limit: undefined,
      credit_card_limit_min: formData.credit_card_limit_min,
      credit_card_limit_max: formData.credit_card_limit_max,
      preferred_terms_amount: undefined,
      preferred_terms_amount_min: formData.preferred_terms_amount_min,
      preferred_terms_amount_max: formData.preferred_terms_amount_max,
      marketing_amount: undefined,
      marketing_amount_min: formData.marketing_amount_min,
      marketing_amount_max: formData.marketing_amount_max,
      store_square_footage: store_square_footage
        ? Math.round(store_square_footage)
        : store_square_footage,
      store_square_meterage: store_square_meterage
        ? Math.round(store_square_meterage)
        : store_square_meterage,
      popup_days_per_week: popup_days_per_week
        ? Number(popup_days_per_week)
        : undefined,
      website_url_social_media_combined:
        formData.website_url_social_media_combined,
      date_of_birth: date_of_birth ? Number(date_of_birth) : undefined,
    };
  };

  const submitFullApplication = () => {
    return submitOpenWithFaireApplication(
      IRetailerLaunchWithFaireApplicationRequest.build({
        application: mapFormDataToRequestData(),
        ...attachDocumentTokens(
          formData,
          [
            "documents",
            "lease_agreement_documents",
            "business_tax_documents",
            "pop_up_proof_documents",
          ],
          [...visibleStoreInfoKeys, ...visibleAdditionalInfoKeys]
        ),
        website_proof_document_tokens: visibleStoreInfoKeys.includes(
          "online_website_proof_documents"
        )
          ? formDocumentToken(formData.online_website_proof_documents)
          : [],
      })
    );
  };

  const submitSoftCreditCheckApplication = () => {
    return submitOpenWithFaireSoftCreditCheckApplication(
      IRetailerLaunchWithFaireSoftCreditCheckApplicationRequest.build({
        application: mapFormDataToRequestData(),
        ...attachDocumentTokens(
          formData,
          [
            "documents",
            "lease_agreement_documents",
            "business_tax_documents",
            "pop_up_proof_documents",
          ],
          [...visibleStoreInfoKeys, ...visibleAdditionalInfoKeys]
        ),
        website_proof_document_tokens: visibleStoreInfoKeys.includes(
          "online_website_proof_documents"
        )
          ? formDocumentToken(formData.online_website_proof_documents)
          : [],
      })
    );
  };

  const submitNMIApplication = () => {
    return submitOpenWithFaireAdditionalInfoApplication(
      IRetailerLaunchWithFaireApplicationRequest.build({
        application: mapFormDataToRequestData(),
      })
    );
  };

  const getSubmitFunction = () => {
    if (isNMIApplication) {
      return submitNMIApplication;
    }

    if (isSoftCreditCheckApplication) {
      return submitSoftCreditCheckApplication;
    }

    return submitFullApplication;
  };

  const submit = async (): Promise<boolean> => {
    trackEvent(`${eventPathBase}/submit`);
    if (!validate().isValid) {
      return false;
    }

    const submitFunction = getSubmitFunction();

    try {
      await submitFunction();
      await refetchRetailer();
      storage.clearData();
      const pixelCustomDataParams = IPixelCustomDataParameters.build({
        retailer_token: retailerToken,
        store_type: retailer?.business_type,
        years_in_business: retailer?.reported_creation_time,
        twitter_click_id: getTwitterClickId(),
      });
      trackOpenWithFaireOpenWithFaireApplicationSubmitSubmit(
        pixelCustomDataParams
      );
      trackOpenWithFaireSubmitApplicationTierClick(
        formVersion,
        `${!!args?.isSignUp}`
      );
      return true;
    } catch (error) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        newErrors.emailToken = "";
        if (error instanceof WebApiError && error.responseData.entity_token) {
          newErrors.emailToken = error.responseData.entity_token;
        }
        newErrors.message = !canApply ? (
          <StrictLocalMsg
            defaultMessage="You’ve already applied. Please reach out to <LinkFormatting>openwithfaire@faire.com</LinkFormatting> with any questions."
            description={{
              text: "Error message to let retailer know they already applied to Open with Faire, and they can reach out to us with any questions.",
              img: "https://cdn.faire.com/fastly/11ff8879a31a6a5932405079b4e0202ea0e199a198dd499c359c1e0f06a3c7c2.png",
            }}
            values={{
              LinkFormatting: EmailOpenWithFaireLink,
            }}
          />
        ) : (
          getWebApiErrorMessage(
            error,
            strictLocalize,
            getErrorMessage(
              strictLocalize,
              ErrorMessages.PLEASE_REFRESH_AND_TRY_AGAIN
            )
          )
        );

        return newErrors;
      });
      return false;
    }
  };

  return {
    isSignUp,
    basicInfoFields,
    storeInfoFields,
    financialInfoFields,
    additionalInfoFields,
    eventPathBase,
    formData,
    errors,
    validate,
    submit,
    setValue,
    setMoneyRangeValue,
    shouldAutoScroll,
    firstFailureKey,
    setShouldAutoScroll,
    toggleResidentialAddressForm,
  };
};

const convertAddressData = (
  isAddressFormVisible: boolean,
  placeId?: string,
  addressValue?: IAddress
) => {
  if (!isAddressFormVisible) {
    if (placeId || !addressValue) {
      return undefined;
    }

    return IAddress.build({
      address1: `${addressValue}`,
    });
  }

  return addressValue;
};

const parseOpeningRange = (openingRange?: string): string | undefined => {
  if (!openingRange) {
    return undefined;
  }

  if (openingRange === ALREADY_OPEN_VALUE.toString()) {
    // This was intentionally not localized
    // since it will show to Risk Ops only
    return "Already opened";
  }

  // Unix Time Stamp
  if (/\d*/.test(openingRange)) {
    return formatDate(Number(openingRange), "MMMM/yyyy");
  }

  return undefined;
};

const EmailOpenWithFaireLink = (chunk: React.ReactNode) => (
  <Button
    variant="tertiary"
    linkTo="mailto:openwithfaire@faire.com"
    style={{
      color: Core.text.critical,
      textDecorationColor: Core.text.critical,
    }}
  >
    {chunk}
  </Button>
);
